import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export interface SlotOccupancyPeriod {
  startsAt: string;
  endsAt: string;
}
export interface SlotOccupancyTarget {
  propertyId: string;
  roomId: string;
}
export interface InventoryOwnership {
  ownershipId: string;
  epoch: number;
  status: string;
  effectiveFrom: string;
  effectiveUntil: string | null;
  mappingRevision: number;
}
export interface InventoryPin {
  pinType: 'PAYMENT' | 'MOVE';
  status: 'ACTIVE' | 'RELEASED';
  referenceId: string;
  releaseEvidence:
    | 'UNKNOWN'
    | 'APPROVAL_NOT_REQUESTED'
    | 'APPROVAL_REJECTED'
    | 'PRODUCT_COMPLETED'
    | 'COMPENSATION_COMPLETED'
    | 'DESTINATION_CONFIRMED'
    | 'MOVE_CANCELLATION_COMPENSATED'
    | null;
}
export interface InventoryClaim {
  claimId: string;
  status: 'HELD' | 'CONFIRMED' | 'RELEASED' | 'EXPIRED';
  startAt: string;
  endAt: string;
  expiresAt: string;
  expiredAtObservation: boolean;
  ownershipEpoch: number;
  generation: number;
  ownerType: 'BOOKING' | 'ORDER' | 'REDACTED';
  ownerId: string | null;
  bookingId: string | null;
  confirmationId: string | null;
  pins: InventoryPin[];
}
export interface InventoryAccessBlock {
  blockId: string;
  claimId: string;
  claimGeneration: number;
  recoveryOperationId: string;
}
export interface InventoryAccessGroup {
  groupId: string;
  guardGeneration: number | null;
  status: 'BLOCKED' | 'OBSERVED_NO_BLOCKS' | 'UNKNOWN';
  reason:
    | 'CREDENTIAL_RECOVERY_PENDING'
    | 'ACCESS_GUARD_UNAVAILABLE'
    | 'ACCESS_GUARD_CHANGED'
    | null;
  blocks: InventoryAccessBlock[];
}
export interface SlotOccupancy {
  propertyId: string | null;
  roomId: string | null;
  slotId: string;
  startsAt: string;
  endsAt: string;
  topologyStatus: 'CURRENT' | 'UNKNOWN';
  topologyReason: string | null;
  selectionStatus: 'SELECTED' | 'NOT_SELECTED' | 'UNKNOWN';
  selectionVersion: number | null;
  authorityStatus: 'UNKNOWN' | 'STORED_ACTIVE_UNVERIFIED' | 'STORED_NOT_ACTIVE';
  authorityReason: string | null;
  ownerships: InventoryOwnership[];
  observedAt: string;
  claims: InventoryClaim[];
  claimConflictObserved: boolean;
  accessObservationStatus: 'OBSERVED' | 'UNKNOWN';
  accessObservationReason: string | null;
  accessGroups: InventoryAccessGroup[];
  recoveryStatus: 'UNAVAILABLE';
  saleDecision: 'NOT_PROVIDED';
}

const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const text = (value: unknown): value is string => typeof value === 'string';
const nullableText = (value: unknown): value is string | null => value === null || text(value);
const integer = (value: unknown): value is number =>
  Number.isSafeInteger(value) && Number(value) >= 0;
const uuid = (value: unknown): value is string => isUuid(value);
const nullableUuid = (value: unknown): value is string | null => value === null || uuid(value);
const nullableInteger = (value: unknown): value is number | null =>
  value === null || integer(value);
const date = (value: unknown): value is string => isOffsetDateTime(value);
const nullableDate = (value: unknown): value is string | null => value === null || date(value);
const enumValue = <T extends string>(value: unknown, values: readonly T[]): value is T =>
  typeof value === 'string' && values.includes(value as T);
const exact = (value: Record<string, unknown>, keys: readonly string[]) =>
  Object.keys(value).length === keys.length && keys.every((key) => key in value);
export const ownershipStates = [
  'DISCOVERED',
  'ELIGIBILITY_CHECKED',
  'FENCE_PREPARED',
  'BARRIER_CHECKED',
  'ACTIVE',
  'SUSPENDED',
  'DRAINING',
  'RETURN_READY',
  'RETURNED',
] as const;
const releaseEvidence = [
  'UNKNOWN',
  'APPROVAL_NOT_REQUESTED',
  'APPROVAL_REJECTED',
  'PRODUCT_COMPLETED',
  'COMPENSATION_COMPLETED',
  'DESTINATION_CONFIRMED',
  'MOVE_CANCELLATION_COMPENSATED',
] as const;
const failure = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '기간별 객실 점유 정보를 불러오지 못했습니다. 관리자에게 문의해 주세요.',
  });
const periodIsValid = (period: SlotOccupancyPeriod) => {
  if (!date(period.startsAt) || !date(period.endsAt)) return false;
  const starts = Date.parse(period.startsAt);
  const ends = Date.parse(period.endsAt);
  return ends > starts && ends - starts <= 366 * 24 * 60 * 60 * 1000;
};
function parsePin(value: unknown): InventoryPin {
  if (
    !record(value) ||
    !exact(value, ['pin_type', 'status', 'reference_id', 'release_evidence']) ||
    !enumValue(value.pin_type, ['PAYMENT', 'MOVE'] as const) ||
    !enumValue(value.status, ['ACTIVE', 'RELEASED'] as const) ||
    !uuid(value.reference_id) ||
    !(value.release_evidence === null || enumValue(value.release_evidence, releaseEvidence))
  )
    throw failure();
  return {
    pinType: value.pin_type,
    status: value.status,
    referenceId: value.reference_id,
    releaseEvidence: value.release_evidence,
  };
}
function parseOwnership(value: unknown): InventoryOwnership {
  if (
    !record(value) ||
    !exact(value, [
      'ownership_id',
      'epoch',
      'status',
      'effective_from',
      'effective_until',
      'mapping_revision',
    ]) ||
    !uuid(value.ownership_id) ||
    !integer(value.epoch) ||
    !enumValue(value.status, ownershipStates) ||
    !date(value.effective_from) ||
    !nullableDate(value.effective_until) ||
    !integer(value.mapping_revision)
  )
    throw failure();
  return {
    ownershipId: value.ownership_id,
    epoch: value.epoch,
    status: value.status,
    effectiveFrom: value.effective_from,
    effectiveUntil: value.effective_until,
    mappingRevision: value.mapping_revision,
  };
}
function parseClaim(value: unknown): InventoryClaim {
  if (
    !record(value) ||
    !exact(value, [
      'claim_id',
      'status',
      'start_at',
      'end_at',
      'expires_at',
      'expired_at_observation',
      'ownership_epoch',
      'generation',
      'owner_type',
      'owner_id',
      'booking_id',
      'confirmation_id',
      'pins',
    ]) ||
    !uuid(value.claim_id) ||
    !enumValue(value.status, ['HELD', 'CONFIRMED', 'RELEASED', 'EXPIRED'] as const) ||
    !date(value.start_at) ||
    !date(value.end_at) ||
    !date(value.expires_at) ||
    typeof value.expired_at_observation !== 'boolean' ||
    !integer(value.ownership_epoch) ||
    !integer(value.generation) ||
    !enumValue(value.owner_type, ['BOOKING', 'ORDER', 'REDACTED'] as const) ||
    !nullableUuid(value.owner_id) ||
    !nullableUuid(value.booking_id) ||
    !nullableUuid(value.confirmation_id) ||
    !Array.isArray(value.pins)
  )
    throw failure();
  return {
    claimId: value.claim_id,
    status: value.status,
    startAt: value.start_at,
    endAt: value.end_at,
    expiresAt: value.expires_at,
    expiredAtObservation: value.expired_at_observation,
    ownershipEpoch: value.ownership_epoch,
    generation: value.generation,
    ownerType: value.owner_type,
    ownerId: value.owner_id,
    bookingId: value.booking_id,
    confirmationId: value.confirmation_id,
    pins: value.pins.map(parsePin),
  };
}
function parseAccessBlock(value: unknown): InventoryAccessBlock {
  if (
    !record(value) ||
    !exact(value, ['block_id', 'claim_id', 'claim_generation', 'recovery_operation_id']) ||
    !uuid(value.block_id) ||
    !uuid(value.claim_id) ||
    !integer(value.claim_generation) ||
    !uuid(value.recovery_operation_id)
  )
    throw failure();
  return {
    blockId: value.block_id,
    claimId: value.claim_id,
    claimGeneration: value.claim_generation,
    recoveryOperationId: value.recovery_operation_id,
  };
}
function parseAccessGroup(value: unknown): InventoryAccessGroup {
  if (
    !record(value) ||
    !exact(value, ['group_id', 'guard_generation', 'status', 'reason', 'blocks']) ||
    !uuid(value.group_id) ||
    !nullableInteger(value.guard_generation) ||
    !enumValue(value.status, ['BLOCKED', 'OBSERVED_NO_BLOCKS', 'UNKNOWN'] as const) ||
    !(
      value.reason === null ||
      enumValue(value.reason, [
        'CREDENTIAL_RECOVERY_PENDING',
        'ACCESS_GUARD_UNAVAILABLE',
        'ACCESS_GUARD_CHANGED',
      ] as const)
    ) ||
    !Array.isArray(value.blocks)
  )
    throw failure();
  return {
    groupId: value.group_id,
    guardGeneration: value.guard_generation,
    status: value.status,
    reason: value.reason,
    blocks: value.blocks.map(parseAccessBlock),
  };
}
function parseOccupancy(value: unknown): SlotOccupancy {
  if (
    !record(value) ||
    !exact(value, [
      'property_id',
      'room_id',
      'slot_id',
      'starts_at',
      'ends_at',
      'topology_status',
      'topology_reason',
      'selection_status',
      'selection_version',
      'authority_status',
      'authority_reason',
      'ownerships',
      'observed_at',
      'claims',
      'claim_conflict_observed',
      'access_observation_status',
      'access_observation_reason',
      'access_groups',
      'recovery_status',
      'sale_decision',
    ]) ||
    !nullableUuid(value.property_id) ||
    !nullableUuid(value.room_id) ||
    !uuid(value.slot_id) ||
    !date(value.starts_at) ||
    !date(value.ends_at) ||
    !enumValue(value.topology_status, ['CURRENT', 'UNKNOWN'] as const) ||
    !nullableText(value.topology_reason) ||
    !enumValue(value.selection_status, ['SELECTED', 'NOT_SELECTED', 'UNKNOWN'] as const) ||
    !nullableInteger(value.selection_version) ||
    !enumValue(value.authority_status, [
      'UNKNOWN',
      'STORED_ACTIVE_UNVERIFIED',
      'STORED_NOT_ACTIVE',
    ] as const) ||
    !nullableText(value.authority_reason) ||
    !Array.isArray(value.ownerships) ||
    !date(value.observed_at) ||
    !Array.isArray(value.claims) ||
    typeof value.claim_conflict_observed !== 'boolean' ||
    !enumValue(value.access_observation_status, ['OBSERVED', 'UNKNOWN'] as const) ||
    !nullableText(value.access_observation_reason) ||
    !Array.isArray(value.access_groups) ||
    value.recovery_status !== 'UNAVAILABLE' ||
    value.sale_decision !== 'NOT_PROVIDED'
  )
    throw failure();
  return {
    propertyId: value.property_id,
    roomId: value.room_id,
    slotId: value.slot_id,
    startsAt: value.starts_at,
    endsAt: value.ends_at,
    topologyStatus: value.topology_status,
    topologyReason: value.topology_reason,
    selectionStatus: value.selection_status,
    selectionVersion: value.selection_version,
    authorityStatus: value.authority_status,
    authorityReason: value.authority_reason,
    ownerships: value.ownerships.map(parseOwnership),
    observedAt: value.observed_at,
    claims: value.claims.map(parseClaim),
    claimConflictObserved: value.claim_conflict_observed,
    accessObservationStatus: value.access_observation_status,
    accessObservationReason: value.access_observation_reason,
    accessGroups: value.access_groups.map(parseAccessGroup),
    recoveryStatus: value.recovery_status,
    saleDecision: value.sale_decision,
  };
}

export async function readSlotOccupancy(
  client: ServiceClient,
  slotId: string,
  period: SlotOccupancyPeriod,
  target: SlotOccupancyTarget,
  signal?: AbortSignal,
): Promise<SlotOccupancy> {
  if (!uuid(slotId) || !uuid(target.propertyId) || !uuid(target.roomId) || !periodIsValid(period))
    throw failure();
  const result = parseOccupancy(
    await client.get<unknown>(`admin/inventory/slots/${slotId}/occupancy`, {
      params: { starts_at: period.startsAt, ends_at: period.endsAt },
      signal,
    }),
  );
  if (
    result.slotId.toLowerCase() !== slotId.toLowerCase() ||
    Date.parse(result.startsAt) !== Date.parse(period.startsAt) ||
    Date.parse(result.endsAt) !== Date.parse(period.endsAt) ||
    (result.propertyId !== null &&
      result.propertyId.toLowerCase() !== target.propertyId.toLowerCase()) ||
    (result.roomId !== null && result.roomId.toLowerCase() !== target.roomId.toLowerCase())
  )
    throw failure();
  return result;
}
